import { useEffect, useState } from "react";
import {
  ComposedChart, Area, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import {
  Gauge, Target, SealCheck, XCircle, WarningCircle, Copy, CalendarX, Percent,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card, Loading } from "@/components/riq";

const PERIODS = [
  { k: "7d", l: "7 days" }, { k: "30d", l: "30 days" }, { k: "90d", l: "90 days" }, { k: "all", l: "All time" },
];

export default function AdminAccuracy() {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/accuracy?period=${period}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [period]);

  if (data === false) return <div className="text-muted-foreground">Could not load accuracy report.</div>;

  const dist = data?.distribution || {};
  const distTotal = Math.max(1, (dist.high || 0) + (dist.good || 0) + (dist.fair || 0) + (dist.low || 0));
  const DIST = [
    { k: "high", l: "High (95%+)", c: "#42C97C" },
    { k: "good", l: "Good (85–95%)", c: "#6E3FFC" },
    { k: "fair", l: "Fair (75–85%)", c: "#F5A623" },
    { k: "low", l: "Low (<75%)", c: "#FF6B6B" },
  ];

  return (
    <div className="space-y-6" data-testid="admin-accuracy">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button key={p.k} data-testid={`accuracy-period-${p.k}`} onClick={() => setPeriod(p.k)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${period === p.k ? "bg-riq-primary text-white" : "border border-riq-border text-neutral-600 hover:border-riq-primary"}`}>
            {p.l}
          </button>
        ))}
      </div>

      {!data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={SealCheck} label="Clean scans" value={`${data.clean_ratio}%`} sub={`${data.clean} of ${data.total}`} tone="ok" />
            <Kpi icon={XCircle} label="Rejection ratio" value={`${data.rejected_ratio}%`} sub={`${data.rejected} of ${data.total}`} tone={data.rejected_ratio >= 10 ? "warn" : "ok"} />
            <Kpi icon={WarningCircle} label="Needs review" value={data.needs_review} sub="flagged for a check" />
            <Kpi icon={Gauge} label="Avg confidence" value={`${data.avg_confidence.total}%`} sub="on the total field" />
          </div>

          {/* Confidence by field */}
          <Card data-testid="accuracy-fields">
            <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2 mb-4">
              <Target size={18} weight="duotone" /> Average AI confidence by field
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {["total", "date", "items", "merchant"].map((f) => (
                <div key={f} className="rounded-xl bg-secondary/50 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{f}</div>
                  <div className="font-head text-2xl font-bold text-riq-primary mt-0.5">{data.avg_confidence[f]}%</div>
                  <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full rounded-full bg-riq-primary" style={{ width: `${data.avg_confidence[f]}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Distribution */}
            <Card data-testid="accuracy-distribution">
              <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2 mb-4">
                <Percent size={18} weight="duotone" /> Confidence distribution
              </h3>
              <div className="space-y-3">
                {DIST.map((d) => {
                  const v = dist[d.k] || 0;
                  return (
                    <div key={d.k} data-testid={`dist-${d.k}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium">{d.l}</span>
                        <span className="text-muted-foreground text-xs">{v} · {Math.round((v / distTotal) * 100)}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-secondary overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(v / distTotal) * 100}%`, background: d.c }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Flag breakdown */}
            <Card data-testid="accuracy-flags">
              <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2 mb-4">
                <WarningCircle size={18} weight="duotone" /> Flag breakdown
              </h3>
              <div className="space-y-3">
                <FlagRow icon={Copy} label="Duplicate receipts" value={data.flags.duplicates} />
                <FlagRow icon={CalendarX} label="Stale / old dates" value={data.flags.stale_date} />
                <FlagRow icon={Gauge} label="Low total confidence" value={data.flags.low_confidence} />
                <FlagRow icon={XCircle} label="Rejected" value={data.rejected} danger />
              </div>
            </Card>
          </div>

          {/* Trend */}
          <Card data-testid="accuracy-trend">
            <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2 mb-4">
              <Gauge size={18} weight="duotone" /> Accuracy over time
            </h3>
            {data.daily.length ? (
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={data.daily} margin={{ left: -18, right: 4, top: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="acc-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6E3FFC" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#6E3FFC" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
                  <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => d?.slice(5)} minTickGap={24} />
                  <YAxis yAxisId="l" domain={[0, 100]} tick={{ fontSize: 9 }} tickFormatter={(v) => `${v}%`} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 9 }} allowDecimals={false} />
                  <Tooltip formatter={(v, n) => (n === "avg_confidence" ? [`${v}%`, "avg confidence"] : [v, n])} />
                  <Area yAxisId="l" type="monotone" dataKey="avg_confidence" stroke="#6E3FFC" strokeWidth={2} fill="url(#acc-grad)" />
                  <Bar yAxisId="r" dataKey="rejected" fill="#FF6B6B" radius={[3, 3, 0, 0]} barSize={10} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : <div className="text-sm text-muted-foreground py-6 text-center">No data for this period.</div>}
            <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-riq-primary" /> Avg confidence %</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#FF6B6B" }} /> Rejected scans</span>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone }) {
  const color = tone === "warn" ? "text-riq-accent" : "text-riq-primary";
  return (
    <Card className="p-4">
      <Icon size={18} weight="duotone" className={`${color} mb-2`} />
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-head text-2xl font-bold mt-0.5 truncate ${color}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

function FlagRow({ icon: Icon, label, value, danger }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <Icon size={18} weight="duotone" className={danger ? "text-riq-accent" : "text-riq-primary"} />
      <span className="flex-1 font-medium">{label}</span>
      <span className={`font-bold ${danger && value > 0 ? "text-riq-accent" : "text-riq-primary"}`}>{value}</span>
    </div>
  );
}
